import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Button, Card, EmptyState, Input, Modal, NoticeBox, Select, Tags } from "../../ui";
import { useConfirmedAction } from "../useConfirmedAction";
import { useSubmitGuard } from "../../modals/modalHooks";
import { categoryLabel } from "../../copilot/copilotText";
import { archiveSavedReplyAPI, createSavedReplyAPI, listSavedRepliesAPI, updateSavedReplyAPI } from "../../../utils/api/copilotAdmin";
import { fillTemplate, settingsError } from "./copilotSettingsText";

const BLANK = { title: "", body: "", lang: "any", category: "" };

const categoryOptions = (t, categories, lang, allLabel) => [
  { value: "", label: allLabel },
  ...(categories || []).map((cat) => ({ value: cat.value, label: lang === "ar" ? cat.labelAr : cat.labelEn })),
];

function ReplyEditor({ t, lang, categories, reply, agentName, onSaved, onClose }) {
  const confirmation = useConfirmedAction(lang);
  const formId = useId();
  const [form, setForm] = useState(reply ? { title: reply.title, body: reply.body, lang: reply.lang, category: reply.category } : BLANK);
  const [errors, setErrors] = useState({});
  const [saving, run] = useSubmitGuard();
  const [failure, setFailure] = useState(null);

  const previewLang = form.lang === "any" ? lang : form.lang;
  const preview = fillTemplate(form.body, { customer_name: t.auto.sampleName, reference: "COP-000123", agent_name: agentName || t.replies.sampleAgent });

  const submit = (event) => {
    event.preventDefault();
    const found = {};
    if (form.title.trim().length < 2) found.title = t.replies.titleRequired;
    if (!form.body.trim()) found.body = t.replies.bodyRequired;
    else if (form.body.length > 5000) found.body = t.replies.bodyLong;
    setErrors(found);
    if (Object.keys(found).length) return;
    confirmation.ask(() => run(async () => {
      try {
        const payload = { title: form.title.trim(), body: form.body.trim(), lang: form.lang, category: form.category };
        if (reply) await updateSavedReplyAPI(reply.id, payload);
        else await createSavedReplyAPI(payload);
        onSaved(reply ? t.replies.updated : t.replies.created);
      } catch (error) {
        setFailure(settingsError(error, t));
        return false;
      }
    }), reply ? "save" : "create", reply ? t.replies.editorTitleEdit : t.replies.editorTitleNew);
  };

  return (
    <><Modal
      title={reply ? t.replies.editorTitleEdit : t.replies.editorTitleNew}
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <Button variant="neutral" appearance="outline" disabled={saving} onClick={onClose}>{t.cancel}</Button>
          <Button type="submit" form={formId} variant="primary" appearance="filled" loading={saving}>{t.save}</Button>
        </>
      }
    >
      {reply?.source === "seed" && !reply.edited && <NoticeBox variant="info" size="sm">{t.replies.seedEdited}</NoticeBox>}
      <form id={formId} className="cs-form" onSubmit={submit} noValidate>
        <Input label={t.replies.fieldTitle} value={form.title} maxLength={80} autoFocus onChange={(e) => setForm({ ...form, title: e.target.value })} error={errors.title || false} />
        <div className="cs-field-row">
          <Select
            label={t.replies.fieldLang}
            value={form.lang}
            onChange={(e) => setForm({ ...form, lang: e.target.value })}
            options={[{ value: "any", label: t.replies.anyLang }, { value: "ar", label: t.arabic }, { value: "en", label: t.english }]}
          />
          <Select label={t.replies.fieldCategory} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} options={categoryOptions(t, categories, lang, t.replies.anyCategory)} />
        </div>
        <Input
          textarea
          rows={6}
          label={t.replies.fieldBody}
          dir={previewLang === "ar" ? "rtl" : "ltr"}
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          error={errors.body || false}
          helperText={t.replies.placeholderHelp}
        />
        {preview && (
          <div className="cs-preview" dir={previewLang === "ar" ? "rtl" : "ltr"}>
            <span className="cs-preview-note">{t.replies.previewTitle}</span>
            <div className="cs-preview-bubble">{preview}</div>
          </div>
        )}
        {failure && <EmptyState variant="error" compact>{failure}</EmptyState>}
      </form>
    </Modal>{confirmation.dialog}</>
  );
}

const DEFAULT_FILTERS = { search: "", category: "", lang: "", source: "" };

// Saved replies: filter (server side), preview, create / edit / delete for managers.
export default function SavedRepliesSettings({ t, lang, categories, canManage, agentName, onNotice }) {
  const confirmation = useConfirmedAction(lang);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [state, setState] = useState({ status: "loading", items: [], error: null });
  const [editor, setEditor] = useState(null); // null | { reply? }
  const ticket = useRef(0);

  const load = useCallback(async () => {
    const mine = (ticket.current += 1);
    setState((prev) => ({ ...prev, status: prev.items.length ? "ready" : "loading", error: null }));
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      const data = await listSavedRepliesAPI(params);
      if (mine === ticket.current) setState({ status: "ready", items: data.replies, error: null });
    } catch (error) {
      if (mine === ticket.current) setState((prev) => ({ ...prev, status: prev.items.length ? "ready" : "error", error: settingsError(error, t) }));
    }
  }, [filters, t]);

  useEffect(() => {
    const timer = setTimeout(load, filters.search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, filters.search]);

  const set = (key) => (event) => setFilters((prev) => ({ ...prev, [key]: event.target.value }));
  const filtered = Object.values(filters).some(Boolean);

  const archive = (reply) => confirmation.ask(async () => {
    try {
      await archiveSavedReplyAPI(reply.id);
      onNotice({ type: "success", text: t.replies.archived });
      load();
    } catch (error) {
      onNotice({ type: "error", text: settingsError(error, t) });
      return false;
    }
  }, "archive", t.replies.confirmArchiveTitle, true, t.replies.confirmArchive(reply.title));

  return (
    <div className="cs-stack">
      <Card
        title={t.replies.title}
        subtitle={t.replies.description}
        hoverable={false}
        actions={canManage ? <Button size="sm" variant="primary" appearance="filled" icon={<Plus size={14} />} onClick={() => setEditor({})}>{t.replies.newReply}</Button> : null}
      >
        {!canManage && <NoticeBox variant="info" size="sm">{t.viewOnly}</NoticeBox>}
        <div className="cs-filters" role="search">
          <Input type="search" iconStart={<Search size={16} />} value={filters.search} onChange={set("search")} placeholder={t.replies.search} aria-label={t.replies.search} maxLength={60} />
          <Select label={t.replies.category} value={filters.category} onChange={set("category")} options={categoryOptions(t, categories, lang, t.replies.allCategories)} />
          <Select
            label={t.replies.language}
            value={filters.lang}
            onChange={set("lang")}
            options={[{ value: "", label: t.replies.allLanguages }, { value: "ar", label: t.arabic }, { value: "en", label: t.english }]}
          />
          <Select
            label={t.replies.source}
            value={filters.source}
            onChange={set("source")}
            options={[{ value: "", label: t.replies.allSources }, { value: "seed", label: t.replies.system }, { value: "custom", label: t.replies.custom }]}
          />
        </div>
      </Card>

      <div aria-busy={state.status === "loading"}>
        {state.status === "loading" && <EmptyState variant="loading" compact>{t.loading}</EmptyState>}
        {state.status === "error" && (
          <EmptyState variant="error" action={<Button size="sm" variant="neutral" appearance="outline" onClick={load}>{t.retry}</Button>}>{state.error}</EmptyState>
        )}
        {state.status === "ready" && state.items.length === 0 && (
          <EmptyState action={filtered ? <Button size="sm" variant="neutral" appearance="outline" onClick={() => setFilters(DEFAULT_FILTERS)}>{t.replies.clearFilters}</Button> : null}>
            {filtered ? t.replies.noMatch : canManage ? t.replies.emptyManager : t.replies.empty}
          </EmptyState>
        )}
        <ul className="cs-reply-grid">
          {state.items.map((reply) => {
            const previewLang = reply.lang === "any" ? lang : reply.lang;
            return (
              <li key={reply.id}>
                <Card hoverable={false} className="cs-reply-card">
                  <div className="cs-reply-head">
                    <h4>{reply.title}</h4>
                    <div className="cs-tags">
                      <Tags size="sm" variant={reply.source === "seed" ? "info" : "primary"}>{reply.source === "seed" ? (reply.edited ? t.replies.customized : t.replies.system) : t.replies.custom}</Tags>
                      <Tags size="sm" variant="neutral">{reply.lang === "any" ? t.replies.anyLang : reply.lang === "ar" ? t.arabic : t.english}</Tags>
                      <Tags size="sm" variant="neutral">{reply.category ? categoryLabel(categories, reply.category, lang) : t.replies.anyCategory}</Tags>
                    </div>
                  </div>
                  <p className="cs-reply-body" dir={previewLang === "ar" ? "rtl" : "ltr"}>{reply.body}</p>
                  <div className="cs-reply-foot">
                    <span className="cs-muted">{t.replies.used(reply.usageCount)}</span>
                    {canManage && (
                      <span className="cs-icons">
                        <Button size="sm" variant="neutral" appearance="ghost" aria-label={`${t.replies.edit}: ${reply.title}`} title={t.replies.edit} icon={<Pencil size={15} />} onClick={() => setEditor({ reply })} />
                        <Button size="sm" variant="danger" appearance="ghost" aria-label={`${t.replies.archive}: ${reply.title}`} title={t.replies.archive} icon={<Trash2 size={15} />} onClick={() => archive(reply)} />
                      </span>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      </div>

      {editor && (
        <ReplyEditor
          t={t}
          lang={lang}
          categories={categories}
          reply={editor.reply}
          agentName={agentName}
          onClose={() => setEditor(null)}
          onSaved={(text) => { setEditor(null); onNotice({ type: "success", text }); load(); }}
        />
      )}
      {confirmation.dialog}
    </div>
  );
}
